<template>
  <div
    v-for="(viewAngle, viewAngleIndex) in staticViewAngles"
    :key="buildKey(viewAngleIndex)"
    :class="bem('slide')"
  >
    <button
      :class="bem('image')"
      @click="launchFullscreenFromGrid(viewAngle, viewAngleIndex)"
    >
      <StaticThreeSixtyFrame
        v-if="show360"
        :key="`static-image-360-${viewAngle}`"
        :scale="customImageScaling[viewAngle]"
        :customised-product="customisedProduct"
        :show-iris-preview="showIrisPreview"
        :frame-number="getFrameNumber(viewAngle)"
        :show-secondary="viewAngle === 5"
        :show-background="true"
      />

      <ProductImg
        v-else
        :key="`static-image-product-${viewAngle}`"
        :view-angle="viewAngle"
        :customised-product="customisedProduct"
        :show-iris-preview="showIrisPreview"
      />

      <div
        v-if="limitedEditionLogo && viewAngleIndex === 0"
        :class="bem('limited-edition-logo')"
        :style="logoWidth ? { width: `${logoWidth}%` } : null"
      >
        <DatoCmsImage
          v-if="limitedEditionLogo"
          :key="limitedEditionLogo.responsiveImage.src"
          :data="{
            ...limitedEditionLogo.responsiveImage,
            sizes: '(max-width: 800px) 100vw, 1000px',
          }"
          :lazy-load="false"
        />
      </div>

      <div v-if="selectedStrapSwatchImage" :class="bem('strap')">
        <DatoCmsImage
          v-if="selectedStrapSwatchImage"
          :key="selectedStrapSwatchImage.responsiveImage.src"
          :data="{
            ...selectedStrapSwatchImage.responsiveImage,
            sizes: '(max-width: 800px) 100vw, 1000px',
          }"
          :lazy-load="false"
          :fade-in-duration="0"
        />
      </div>
    </button>
  </div>
  <div
    v-for="(
      lifestyleImage, lifestyleImageIndex
    ) in staticProductAndLifestyleImages"
    :key="buildLifestyleKey(lifestyleImageIndex)"
    :class="bem('slide')"
  >
    <button
      :class="bem('lifestyle-image-wrapper')"
      @click="
        launchFullscreenFromGrid(
          null,
          lifestyleImageIndex + staticViewAngles.length
        )
      "
    >
      <DatoCmsImage
        v-if="lifestyleImage?.responsiveImage?.srcSet"
        :key="lifestyleImage.responsiveImage.src"
        :data="lifestyleImage.responsiveImage"
        object-fit="cover"
        :object-position="`${lifestyleImage.focalPoint.x * 100}% ${
          lifestyleImage.focalPoint.y * 100
        }%`"
        :class="bem('image', {
          lifestyle: true,
          scaleUp: lifestyleImage.isStaticProductImage && lifestyleImageIndex === 0
        })"
        sizes="(max-width: 600px) 100vw, 40vw"
      />
    </button>

    <div
      v-if="limitedEditionLogo && lifestyleImageIndex === 0 && !staticViewAngles.length"
      :class="bem('limited-edition-logo')"
      :style="logoWidth ? { width: `${logoWidth}%` } : null"
    >
      <DatoCmsImage
        v-if="limitedEditionLogo"
        :key="limitedEditionLogo.responsiveImage.src"
        :data="{
          ...limitedEditionLogo.responsiveImage,
          sizes: '(max-width: 800px) 100vw, 1000px',
        }"
        :lazy-load="false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type CustomisedProduct from '~/models/CustomisedProduct'
import { Image as DatoCmsImage } from 'vue-datocms'

interface ProductGalleryThumbsProps {
  activeSlideIndex: number
  staticViewAngles?: Array<number>
  customImageScaling?: object
  customisedProduct: typeof CustomisedProduct
  staticProductAndLifestyleImages?: Array<object>
  showIrisPreview: boolean
  show360: boolean
  viewAngleMapping: object
}

const props = withDefaults(defineProps<ProductGalleryThumbsProps>(), {
  staticViewAngles: () => [],
  customImageScaling: () => ({}),
  staticProductAndLifestyleImages: () => [],
  showIrisPreview: false,
  show360: false,
  viewAngleMapping: () => ({}),
})

const emits = defineEmits(['launch-fullscreen-product-modal'])

const bem = useBEM('ProductGallerySlides')

function buildKey(index) {
  return 'slide-' + index
}

function buildLifestyleKey(index) {
  return 'lifestyle-slide-' + index
}

function getFrameNumber(viewAngle) {
  return props.viewAngleMapping[viewAngle] || 1
}

function launchFullscreenFromGrid(viewAngle, index) {
  emits('launch-fullscreen-product-modal', { viewAngle, index })
}

const limitedEditionLogo = computed(() => {
  return props.customisedProduct.product.limitedEditionLogo
})

const logoWidth = computed(() => {
  return props.customisedProduct?.product?.logoWidth
})

const selectedStrapSwatchImage = computed(() => {
  return Object.values(
    props.customisedProduct.choices
  ).find(choice => choice.partType === 'strap')?.swatch?.strapImage
})
</script>

<style lang="postcss" scoped>
.ProductGallerySlides__slide {
  @apply flex-shrink-0 flex-grow-0 basis-full h-full relative;

  @screen smMax {
    background: linear-gradient(180deg, #fff 55.39%, #f7f7fa 87.56%);
  }
}

.ProductGallerySlides__lifestyle-image-wrapper {
  @apply block h-full;
}

.ProductGallerySlides__image {
  @apply flex flex-col w-full h-full;

  cursor: url('data:image/svg+xml,%3Csvg%20width%3D%2240%22%20height%3D%2240%22%20viewBox%3D%220%200%2040%2040%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22white%22%20transform%3D%22translate(12%2012)%22%2F%3E%3Cpath%20d%3D%22M0%2020C0%208.95431%208.95431%200%2020%200C31.0457%200%2040%208.95431%2040%2020C40%2031.0457%2031.0457%2040%2020%2040C8.95431%2040%200%2031.0457%200%2020Z%22%20fill%3D%22white%22%2F%3E%3Cpath%20d%3D%22M12%2027.9733V22.4958H13.7143V26.2705H17.5143V27.9733H12ZM12%2017.5575V12.08H17.5143V13.7829H13.7143V17.5575H12ZM22.4857%2027.9733V26.2705H26.2857V22.4958H28V27.9733H22.4857ZM26.2857%2017.5575V13.7829H22.4857V12.08H28V17.5575H26.2857Z%22%20fill%3D%22%23111111%22%2F%3E%3C%2Fsvg%3E') 20 20, auto !important;

  &:not(&--lifestyle) {
    @screen mdMax {
      @apply relative w-[90%] h-[90%] mx-auto;
    }
  }

  &--scale-up {
    @apply scale-[115%];
  }
}

.ProductGallerySlides__limited-edition-logo {
  @apply w-[30%] md:w-1/5 xl:w-1/4
    mr-sm absolute bottom-sm md:bottom-sm -right-6 md:right-sm block;

  picture {
    @apply object-cover;
  }
}

.ProductGallerySlides__strap {
  @apply mx-auto mb-6;
}
</style>
